import { useCallback, useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  MAX_IMAGE_DATA_LENGTH,
  NOTE_EMOJIS,
  NOTE_FONTS,
  PAPER_COLORS,
  PEN_COLORS,
  PIN_COLORS,
  paperColorByKey,
  pinColorByKey,
} from "../../util/boardConstants";

/** Internal drawing resolution; the canvas is scaled down responsively via CSS. */
const CANVAS_SIZE = 720;
/** Exported PNG resolution — enough for the enlarged detail view, small enough to store. */
const EXPORT_SIZE = 480;
const MAX_UNDO_STEPS = 20;

type Tool = "pen" | "marker" | "rainbow" | "eraser" | "text" | "emoji";

interface TextInputState {
  /** Position in canvas coordinates. */
  x: number;
  y: number;
  /** canvas-px → displayed-px ratio at the moment of placement. */
  displayScale: number;
  value: string;
}

interface NoteEditorModalProps {
  authorName: string;
  onSave: (imageData: string, paperColor: string, pinColor: string, poll: boolean) => void;
  onClose: () => void;
}

function NoteEditorModal({ authorName, onSave, onClose }: NoteEditorModalProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  const [tool, setTool] = useState<Tool>("pen");
  const [penColor, setPenColor] = useState(PEN_COLORS[0]);
  const [strokeWidth, setStrokeWidth] = useState(6);
  const [paperColor, setPaperColor] = useState(PAPER_COLORS[0].key);
  const [pinColor, setPinColor] = useState(PIN_COLORS[0].key);
  const [selectedEmoji, setSelectedEmoji] = useState(NOTE_EMOJIS[0]);
  const [fontKey, setFontKey] = useState(NOTE_FONTS[0].key);
  const [poll, setPoll] = useState(false);
  const [textInput, setTextInput] = useState<TextInputState | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  const undoStack = useRef<ImageData[]>([]);
  const redoStack = useRef<ImageData[]>([]);
  const drawing = useRef<{ active: boolean; lastX: number; lastY: number; hue: number; pointerId: number | null }>(
    { active: false, lastX: 0, lastY: 0, hue: 0, pointerId: null },
  );

  const getCtx = useCallback(() => canvasRef.current?.getContext("2d") ?? null, []);

  // Block page scroll behind the fullscreen editor (matters on mobile).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const textInputOpen = textInput !== null;
  useEffect(() => {
    if (!textInputOpen) return;
    // Deferred so the focus lands after the tap's whole event sequence — focusing
    // synchronously loses to the browser's default focus handling.
    const id = window.setTimeout(() => textInputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [textInputOpen]);

  const pushUndo = useCallback(() => {
    const ctx = getCtx();
    if (!ctx) return;
    undoStack.current.push(ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE));
    if (undoStack.current.length > MAX_UNDO_STEPS) undoStack.current.shift();
    redoStack.current = [];
    setCanUndo(true);
    setCanRedo(false);
  }, [getCtx]);

  const undo = useCallback(() => {
    const ctx = getCtx();
    const snapshot = undoStack.current.pop();
    if (!ctx || !snapshot) return;
    redoStack.current.push(ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE));
    ctx.putImageData(snapshot, 0, 0);
    setCanUndo(undoStack.current.length > 0);
    setCanRedo(true);
  }, [getCtx]);

  const redo = useCallback(() => {
    const ctx = getCtx();
    const snapshot = redoStack.current.pop();
    if (!ctx || !snapshot) return;
    undoStack.current.push(ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE));
    ctx.putImageData(snapshot, 0, 0);
    setCanRedo(redoStack.current.length > 0);
    setCanUndo(true);
    setHasContent(true);
  }, [getCtx]);

  const clearCanvas = useCallback(() => {
    const ctx = getCtx();
    if (!ctx) return;
    pushUndo();
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    setHasContent(false);
  }, [getCtx, pushUndo]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (textInput) setTextInput(null);
        else onClose();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !textInput) {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [textInput, onClose, undo, redo]);

  const toCanvasCoords = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * CANVAS_SIZE,
      y: ((clientY - rect.top) / rect.height) * CANVAS_SIZE,
      displayScale: rect.width / CANVAS_SIZE,
    };
  };

  const applyStrokeStyle = (ctx: CanvasRenderingContext2D) => {
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.globalCompositeOperation = tool === "eraser" ? "destination-out" : "source-over";
    ctx.globalAlpha = tool === "marker" ? 0.35 : 1;
    if (tool === "marker") ctx.lineWidth = strokeWidth * 4;
    else if (tool === "eraser") ctx.lineWidth = strokeWidth * 5;
    else if (tool === "rainbow") ctx.lineWidth = strokeWidth * 2.5;
    else ctx.lineWidth = strokeWidth * 1.5;
    ctx.strokeStyle = tool === "rainbow" ? `hsl(${drawing.current.hue}, 90%, 55%)` : penColor;
  };

  const stampEmoji = (x: number, y: number) => {
    const ctx = getCtx();
    if (!ctx) return;
    pushUndo();
    // Scales with the slider up to ~90 % of the note at max width.
    const size = strokeWidth * 32;
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
    ctx.font = `${size}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(selectedEmoji, x, y);
    setHasContent(true);
  };

  const commitText = () => {
    if (!textInput) return;
    const value = textInput.value.trim();
    setTextInput(null);
    if (!value) return;
    const ctx = getCtx();
    if (!ctx) return;
    pushUndo();
    const fontSize = 12 + strokeWidth * 6;
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
    ctx.font = `${fontSize}px ${NOTE_FONTS.find((f) => f.key === fontKey)?.css ?? "sans-serif"}`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillStyle = penColor;
    ctx.fillText(value, textInput.x, textInput.y);
    setHasContent(true);
  };

  const onCanvasPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // Suppress the compatibility mouse events (focus stealing, text selection): the
    // mousedown default action would blur the freshly mounted text input, whose
    // blur-commit closes it again before it's ever visible.
    e.preventDefault();
    // Only the first touch draws — a stray second finger must not scribble.
    if (drawing.current.active) return;
    if (textInput) { commitText(); return; }
    const { x, y, displayScale } = toCanvasCoords(e.clientX, e.clientY);
    if (tool === "text") {
      setTextInput({ x, y, displayScale, value: "" });
      return;
    }
    if (tool === "emoji") {
      stampEmoji(x, y);
      return;
    }
    pushUndo();
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = { active: true, lastX: x, lastY: y, hue: drawing.current.hue, pointerId: e.pointerId };
    // Draw a dot for a simple tap.
    const ctx = getCtx();
    if (!ctx) return;
    applyStrokeStyle(ctx);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 0.01, y + 0.01);
    ctx.stroke();
    setHasContent(tool !== "eraser" ? true : hasContent);
  };

  const onCanvasPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const d = drawing.current;
    if (!d.active || e.pointerId !== d.pointerId) return;
    const ctx = getCtx();
    if (!ctx) return;
    const { x, y } = toCanvasCoords(e.clientX, e.clientY);
    const dist = Math.hypot(x - d.lastX, y - d.lastY);
    if (tool === "rainbow") d.hue = (d.hue + dist * 0.6) % 360;
    applyStrokeStyle(ctx);
    ctx.beginPath();
    ctx.moveTo(d.lastX, d.lastY);
    ctx.lineTo(x, y);
    ctx.stroke();
    d.lastX = x;
    d.lastY = y;
  };

  const onCanvasPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.pointerId === drawing.current.pointerId) {
      drawing.current.active = false;
      drawing.current.pointerId = null;
    }
  };

  const save = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = EXPORT_SIZE;
    exportCanvas.height = EXPORT_SIZE;
    const ctx = exportCanvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(canvas, 0, 0, EXPORT_SIZE, EXPORT_SIZE);
    const imageData = exportCanvas.toDataURL("image/png");
    if (imageData.length > MAX_IMAGE_DATA_LENGTH) {
      alert(t("boardImageTooLarge"));
      return;
    }
    onSave(imageData, paperColor, pinColor, poll);
  };

  const paper = paperColorByKey(paperColor);
  const toolButtons: { key: Tool; icon: string }[] = [
    { key: "pen", icon: "✏️" },
    { key: "marker", icon: "🖊️" },
    { key: "rainbow", icon: "🌈" },
    { key: "eraser", icon: "🧽" },
    { key: "text", icon: "🔤" },
    { key: "emoji", icon: "😀" },
  ];

  return (
    <motion.div
      className="note-editor-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      <motion.div
        className="note-editor"
        initial={{ opacity: 0, scale: 0.94, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 12 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        <div className="note-editor-header">
          <span className="note-editor-title">{t("boardEditorTitle")}</span>
          <button type="button" className="note-editor-close" onClick={onClose} aria-label={t("close") as string}>×</button>
        </div>

        <div className="note-editor-body">
          <div
            className="note-editor-canvas-wrap"
            ref={wrapRef}
            style={{ background: `linear-gradient(180deg, ${paper.base}, ${paper.edge})` }}
          >
            <div className="note-editor-canvas-author" style={{ fontFamily: NOTE_FONTS[0].css }}>{authorName}</div>
            <canvas
              ref={canvasRef}
              width={CANVAS_SIZE}
              height={CANVAS_SIZE}
              className={`note-editor-canvas tool-${tool}`}
              onPointerDown={onCanvasPointerDown}
              onPointerMove={onCanvasPointerMove}
              onPointerUp={onCanvasPointerUp}
              onPointerCancel={onCanvasPointerUp}
            />
            {textInput && (
              <div
                className="note-editor-text-input-wrap"
                style={{
                  left: `${(textInput.x / CANVAS_SIZE) * 100}%`,
                  top: `${(textInput.y / CANVAS_SIZE) * 100}%`,
                }}
              >
                <input
                  ref={textInputRef}
                  className="note-editor-text-input"
                  style={{
                    fontSize: (12 + strokeWidth * 6) * textInput.displayScale,
                    fontFamily: NOTE_FONTS.find((f) => f.key === fontKey)?.css,
                    color: penColor,
                  }}
                  placeholder={t("boardTextPlaceholder") as string}
                  value={textInput.value}
                  onChange={(e) => setTextInput({ ...textInput, value: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitText();
                  }}
                  onBlur={commitText}
                />
                <button
                  type="button"
                  className="note-editor-text-confirm"
                  aria-label={t("confirm") as string}
                  // preventDefault keeps the input from blurring (blur would commit first
                  // and unmount this button before its click fires)
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={commitText}
                >
                  ✓
                </button>
              </div>
            )}
          </div>

          <div className="note-editor-controls">
            <div className="note-editor-tools">
              {toolButtons.map((tb) => (
                <button
                  key={tb.key}
                  type="button"
                  className={`note-editor-tool${tool === tb.key ? " active" : ""}`}
                  title={t(`boardTool_${tb.key}`) as string}
                  onClick={() => setTool(tb.key)}
                >
                  <span className="note-editor-tool-icon">{tb.icon}</span>
                  <span className="note-editor-tool-label">{t(`boardTool_${tb.key}`)}</span>
                </button>
              ))}
            </div>

            {tool === "emoji" && (
              <div className="note-editor-hint">💡 {t("boardEmojiHint")}</div>
            )}
            {tool === "emoji" && (
              <div className="note-editor-emojis">
                {NOTE_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className={`note-editor-emoji${selectedEmoji === emoji ? " active" : ""}`}
                    onClick={() => setSelectedEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}

            {tool === "text" && (
              <>
                <div className="note-editor-hint">💡 {t("boardTextHint")}</div>
                <div className="note-editor-row">
                  <span className="note-editor-label">{t("boardFont")}</span>
                  <div className="note-editor-fonts">
                    {NOTE_FONTS.map((font) => (
                      <button
                        key={font.key}
                        type="button"
                        className={`note-editor-font${fontKey === font.key ? " active" : ""}`}
                        style={{ fontFamily: font.css }}
                        onClick={() => setFontKey(font.key)}
                      >
                        Abc
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="note-editor-row">
              <span className="note-editor-label">{t("boardPenColor")}</span>
              <div className="note-editor-swatches">
                {PEN_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`note-editor-swatch${penColor === color ? " active" : ""}`}
                    style={{ background: color }}
                    onClick={() => setPenColor(color)}
                    aria-label={color}
                  />
                ))}
                <input
                  type="color"
                  className="note-editor-color-input"
                  value={penColor}
                  onChange={(e) => setPenColor(e.target.value)}
                  title={t("boardCustomColor") as string}
                />
              </div>
            </div>

            <div className="note-editor-row">
              <span className="note-editor-label">{t("boardStrokeWidth")}</span>
              <input
                type="range"
                min={2}
                max={20}
                value={strokeWidth}
                onChange={(e) => setStrokeWidth(Number(e.target.value))}
                className="note-editor-range"
              />
              <span
                className="note-editor-width-preview"
                style={{ width: strokeWidth + 4, height: strokeWidth + 4, background: penColor }}
              />
            </div>

            <div className="note-editor-row">
              <span className="note-editor-label">{t("boardPaperColor")}</span>
              <div className="note-editor-swatches">
                {PAPER_COLORS.map((color) => (
                  <button
                    key={color.key}
                    type="button"
                    className={`note-editor-swatch paper${paperColor === color.key ? " active" : ""}`}
                    style={{ background: `linear-gradient(180deg, ${color.base}, ${color.edge})` }}
                    onClick={() => setPaperColor(color.key)}
                    aria-label={color.key}
                  />
                ))}
              </div>
            </div>

            <div className="note-editor-row">
              <span className="note-editor-label">{t("boardPinColor")}</span>
              <div className="note-editor-swatches">
                {PIN_COLORS.map((color) => (
                  <button
                    key={color.key}
                    type="button"
                    className={`note-editor-swatch pin${pinColor === color.key ? " active" : ""}`}
                    style={{ background: color.color }}
                    onClick={() => setPinColor(color.key)}
                    aria-label={color.key}
                  />
                ))}
              </div>
            </div>

            <div className="note-editor-row">
              <button
                type="button"
                className={`note-editor-poll-toggle${poll ? " active" : ""}`}
                onClick={() => setPoll(!poll)}
              >
                🗳️ {t("boardPollToggle")}
                <span className="note-editor-poll-state">{poll ? "✓" : ""}</span>
              </button>
            </div>
            {poll && <div className="note-editor-hint">💡 {t("boardPollHint")}</div>}
          </div>
        </div>

        <div className="note-editor-footer">
          <div className="note-editor-history">
            <Button variant="outline-secondary" size="sm" onClick={undo} disabled={!canUndo}>↩ {t("boardUndo")}</Button>
            <Button variant="outline-secondary" size="sm" onClick={redo} disabled={!canRedo}>↪ {t("boardRedo")}</Button>
            <Button variant="outline-danger" size="sm" onClick={clearCanvas} disabled={!hasContent && !canUndo}>🗑 {t("boardClear")}</Button>
          </div>
          <Button variant="primary" onClick={save} disabled={!hasContent}>
            📌 {t("boardPin")}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default NoteEditorModal;
